import type { DragEvent } from 'react';
import { useEffect, useRef, useState } from 'react';

import type { FileIntakeAccept } from './file-intake.types';
import { FileIntakeUtil } from './file-intake-util';

/**
 * The drag mechanics of a drop target: what is in flight, and what landed.
 *
 * **Every handler ignores a drag carrying no files.** Text dragged out of a paragraph fires the
 * same four events, and a target that lit up for it would promise something it cannot take.
 *
 * **`disabled` refuses the drag outright**: no frame, and nothing taken if it lands anyway. The
 * window-level refusal below is deliberately left alone - a box that cannot take a file still must
 * not let a missed drop navigate the page out from under the reader.
 */
export const useFileIntakeDrag = ({
  accept,
  disabled,
  onFiles,
}: {
  accept?: FileIntakeAccept[];
  disabled?: boolean;
  onFiles: (files: { accepted: File[]; rejected: string[] }) => void;
}) => {
  const [isDragging, setIsDragging] = useState(false);

  /*
    A depth count, not a boolean: `dragleave` fires every time the pointer crosses into a child, so
    a boolean turns the frame off over every element the drag passes on its way in.
  */
  const depthRef = useRef(0);

  /*
    **On the window, because the default lives on the document.** A drop that misses this component
    otherwise navigates the page to the file, and whatever the reader was in the middle of is gone.
  */
  useEffect(() => {
    const refuse = (event: globalThis.DragEvent) => {
      if (!FileIntakeUtil.isFileDrag({ transfer: event.dataTransfer })) return;

      event.preventDefault();
    };

    window.addEventListener('dragover', refuse);
    window.addEventListener('drop', refuse);

    return () => {
      window.removeEventListener('dragover', refuse);
      window.removeEventListener('drop', refuse);
    };
  }, []);

  /*
    **A box that has just been refused is not still showing a frame.** Every handler below ignores
    a drag once `disabled` is set, so a frame already raised would have nothing left to lower it -
    neither the `dragleave` nor the `drop` is answered - and it would sit over the content for good.
  */
  useEffect(() => {
    if (!disabled) return;

    depthRef.current = 0;
    setIsDragging(false);
  }, [disabled]);

  const handleDragEnter = (event: DragEvent<HTMLElement>) => {
    if (disabled) return;
    if (!FileIntakeUtil.isFileDrag({ transfer: event.dataTransfer })) return;

    event.preventDefault();
    depthRef.current += 1;
    setIsDragging(true);
  };

  const handleDragOver = (event: DragEvent<HTMLElement>) => {
    if (disabled) return;
    if (!FileIntakeUtil.isFileDrag({ transfer: event.dataTransfer })) return;

    // On every move, because the default action for a dragover is "this element takes no drops".
    event.preventDefault();
    /*
      Says a copy will be made, which is true - the file stays wherever it came from. Left alone, a
      drag out of some applications shows a move cursor and implies the original is going somewhere.
    */
    event.dataTransfer.dropEffect = 'copy';
  };

  /*
    No `disabled` check, unlike its three siblings: the depth only ever rises in `handleDragEnter`,
    which refuses while disabled, and the effect above zeroes it the moment disabled turns on - so
    there is never a leave left to ignore, and a guard here would be unreachable.
  */
  const handleDragLeave = (event: DragEvent<HTMLElement>) => {
    if (!FileIntakeUtil.isFileDrag({ transfer: event.dataTransfer })) return;

    depthRef.current = Math.max(0, depthRef.current - 1);

    if (depthRef.current === 0) setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLElement>) => {
    if (disabled) return;
    if (!FileIntakeUtil.isFileDrag({ transfer: event.dataTransfer })) return;

    event.preventDefault();
    /*
      Zeroed rather than decremented: no `dragleave` follows a drop, so counting down here would
      leave the frame up over a file that has already landed.
    */
    depthRef.current = 0;
    setIsDragging(false);
    onFiles(
      FileIntakeUtil.partition({ accept, files: event.dataTransfer.files })
    );
  };

  return {
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    isDragging,
  };
};
